/**
 * Impostazioni: contratti parametrici e pattern di distribuzione oraria,
 * con matrice di compatibilità calcolata dal backend.
 */
import React from "react";
import { useApi } from "../hooks/useApi";
import {
  Card,
  Badge,
  Spinner,
  ErrorBox,
  SimpleTable,
} from "../components/ui/basics";
import CostiSection from "./impostazioni/CostiSection";
import TipologieTurnoSection from "./impostazioni/TipologieTurnoSection";
import MansioneTurnoSection from "./impostazioni/MansioneTurnoSection";
import GruppiMansioneCostoSection from "./impostazioni/GruppiMansioneCostoSection";

export default function ImpostazioniPage() {
  const contratti = useApi("/planning/contratti");
  const patterns = useApi("/planning/patterns");

  const loading = contratti.loading || patterns.loading;
  const error = contratti.error || patterns.error;

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-gray-800">Impostazioni</h2>
      <p className="text-sm text-gray-500">
        Contratti (CCNL parametrici) e pattern di distribuzione dell'orario.
        {contratti.data?.source === "default" && (
          <span className="block text-yellow-700 mt-1">
            ⚠ Valori di default in sola lettura: le entità Dataverse di
            pianificazione non sono ancora state create (gate schema).
          </span>
        )}
      </p>

      {loading && <Spinner />}
      {error && <ErrorBox message={error} />}

      {contratti.data && (
        <Card title="Contratti">
          <SimpleTable
            rowKey={(r) => r.codice}
            columns={[
              { key: "codice", label: "Codice" },
              { key: "nome", label: "Nome" },
              { key: "oresettimanali", label: "Ore/sett FTE", align: "right" },
              { key: "maxoregiorno", label: "Max h/giorno", align: "right" },
              {
                key: "riposominimoore",
                label: "Riposo min (h)",
                align: "right",
              },
              {
                key: "maxgiorniconsecutivi",
                label: "Max gg consec.",
                align: "right",
              },
              {
                key: "moltiplicatorecostoazienda",
                label: "× costo azienda",
                align: "right",
              },
            ]}
            rows={contratti.data.items}
          />
        </Card>
      )}

      {patterns.data && (
        <Card
          title="Pattern di distribuzione oraria"
          subtitle="Compatibilità calcolata contro ogni contratto attivo"
        >
          <SimpleTable
            rowKey={(r) => r.codice}
            columns={[
              { key: "codice", label: "Codice" },
              { key: "nome", label: "Nome" },
              { key: "giornisettimana", label: "Giorni/sett", align: "right" },
              {
                key: "ore",
                label: "Ore/giorno",
                align: "right",
                render: (r) => `${r.oregiornomin}–${r.oregiornomax}`,
              },
              {
                key: "compatibilita",
                label: "Compatibilità",
                render: (r) => (
                  <div className="space-y-1">
                    {r.compatibilita.map((c) => (
                      <div
                        key={c.contratto}
                        className="flex items-center gap-2"
                      >
                        <Badge status={c.compatibile ? "ok" : "critico"}>
                          {c.contratto}
                        </Badge>
                        <span className="text-xs text-gray-500">
                          {c.oreGiornoTeoriche} h/giorno teoriche
                          {c.issues.map((i) => ` · ${i.message}`).join("")}
                        </span>
                      </div>
                    ))}
                  </div>
                ),
              },
            ]}
            rows={patterns.data.items}
          />
        </Card>
      )}

      <TipologieTurnoSection />

      <MansioneTurnoSection />

      <CostiSection />

      <GruppiMansioneCostoSection />
    </div>
  );
}
